import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { timeZoneConverter } from "mrb/common/utils";
import moment from "moment";
import { mergeOrOverrideCss } from "mrb/common/utils";
import { observer } from "mobx-react";

function NakiScheduleDatePicker({
    value,
    onChange,
    placeholder = "--/--/----",
    isClearable = true,
    disabled,
    minDate,
    maxDate,
    timeZone,
    showTimeSelect,
    timeIntervals,
    onChangeRaw,
    dateFormat,
    classNameExtend,
    classNameOverride,
    t = (i) => i,
}) {
    let internalValue = null;
    if (value) {
        internalValue = timeZone
            ? timeZoneConverter.toJsDate(value, timeZone)
            : moment.utc(value).toDate();
    }

    const [staged, setStagedState] = useState(undefined);
    const stagedRef = useRef(undefined);
    const dateChosenRef = useRef(false);

    function setStaged(val) {
        stagedRef.current = val;
        setStagedState(val);
    }

    const displayValue = showTimeSelect && staged !== undefined ? staged : internalValue;

    function handleSelect() {
        // onSelect fires for calendar day clicks only (not time list clicks).
        // In react-datepicker, onChange fires before onSelect, so we mark
        // dateChosen here (after handleChange already ran) to flag that the
        // staged value came from a date click and is safe to commit on close.
        if (showTimeSelect) {
            dateChosenRef.current = true;
        }
    }

    function handleChange(newValue) {
        if (showTimeSelect) {
            if (!newValue) {
                onChange(undefined);
                setStaged(undefined);
                dateChosenRef.current = false;
                return;
            }
            if (isNaN(newValue.getTime())) return;
            setStaged(newValue);
            return;
        }
        let parsedValue;
        if (newValue) {
            if (isNaN(newValue.getTime())) return;
            parsedValue = timeZone
                ? timeZoneConverter.toISOString(newValue, timeZone)
                : newValue.toISOString();
        }
        onChange(parsedValue);
    }

    function handleCalendarClose() {
        if (!showTimeSelect || stagedRef.current === undefined) return;
        const isEditing = !!value;
        if (!isEditing && !dateChosenRef.current) {
            setStaged(undefined);
            dateChosenRef.current = false;
            return;
        }
        let parsedValue;
        if (stagedRef.current && !isNaN(stagedRef.current.getTime())) {
            parsedValue = timeZone
                ? timeZoneConverter.toISOString(stagedRef.current, timeZone)
                : stagedRef.current.toISOString();
        }
        onChange(parsedValue);
        setStaged(undefined);
        dateChosenRef.current = false;
    }

    return (
        <DatePicker
            selected={displayValue}
            onChange={handleChange}
            onSelect={handleSelect}
            onCalendarClose={handleCalendarClose}
            minDate={minDate}
            maxDate={maxDate}
            placeholderText={t(placeholder)}
            disabled={disabled}
            isClearable={isClearable}
            showTimeSelect={showTimeSelect}
            timeIntervals={timeIntervals}
            onChangeRaw={onChangeRaw}
            dateFormat={dateFormat}
            className={mergeOrOverrideCss("c-input c-input--text c-input--base", classNameOverride, classNameExtend)}
        />
    );
}

NakiScheduleDatePicker.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    isClearable: PropTypes.bool,
    disabled: PropTypes.bool,
    minDate: PropTypes.any,
    maxDate: PropTypes.any,
    timeZone: PropTypes.string,
    showTimeSelect: PropTypes.bool,
    timeIntervals: PropTypes.number,
    onChangeRaw: PropTypes.func,
    dateFormat: PropTypes.string,
    classNameExtend: PropTypes.string,
    classNameOverride: PropTypes.string,
    t: PropTypes.func,
};

export default observer(NakiScheduleDatePicker);
